import React, { useEffect, useState } from 'react'
import { useLanguage } from '../../../components/context/LanguageContext'
import { Card, List, Tag, Typography, Spin, Alert, Breadcrumb, Button } from 'antd'
import {
  FolderOpenOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import { apiRequest } from '../../../helpers'

const { Title } = Typography

const getFileIcon = (fileName, itemType) => {
  if (itemType === 'folder') {
    return <FolderOpenOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
  }

  const extension = fileName.toLowerCase().split('.').pop()
  const iconStyle = { fontSize: '24px' }

  switch (extension) {
    case 'pdf':
      return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />
    case 'doc':
    case 'docx':
      return <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined style={{ ...iconStyle, color: '#52c41a' }} />
    case 'ppt':
    case 'pptx':
      return <FilePptOutlined style={{ ...iconStyle, color: '#fa8c16' }} />
    case 'zip':
    case 'rar':
    case '7z':
      return <FileZipOutlined style={{ ...iconStyle, color: '#722ed1' }} />
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
      return <FileImageOutlined style={{ ...iconStyle, color: '#eb2f96' }} />
    case 'txt':
    case 'md':
    case 'log':
      return <FileTextOutlined style={{ ...iconStyle, color: '#595959' }} />
    default:
      return <FileOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />
  }
}

const LicitatiiStorageTab = ({ agent, refreshData }) => {
  const languageContext = useLanguage()
  const [folders, setFolders] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [currentPath, setCurrentPath] = useState('')
  const [pathHistory, setPathHistory] = useState([''])
  const [rootFolder, setRootFolder] = useState(null)
  console.log('LicitatiiStorageTab agent:', agent)
  useEffect(() => {
    if (agent && agent.id) {
      initializeStorage()
    }
  }, [agent])

  const initializeStorage = async () => {
    setLoading(true)
    setError(null)

    try {
      // First, get the root level to find the first folder
      const result = await apiRequest(process.env.REACT_APP_API + `agents/licitatii/${agent.id}/storage/folders`, 'GET')

      if (!result.ok) {
        throw new Error(result.data?.message || 'Failed to load storage folders')
      }

      const rootItems = result.data || []

      // Find the first folder to use as root
      const firstFolder = rootItems.find(item => item.type === 'folder')

      if (firstFolder) {
        setRootFolder(firstFolder)
        // Load the contents of the first folder as initial display
        await loadStorageFolders(firstFolder.name)
        setCurrentPath('') // Reset path since we're treating this as root
      } else {
        // No folders found, just show the root items
        setFolders(rootItems)
      }
    } catch (error) {
      console.error('[ERROR] Problem initializing storage:', error)
      setError(error.message)
      setFolders([])
    }

    setLoading(false)
  }

  const loadStorageFolders = async (path = '') => {
    setLoading(true)
    setError(null)

    try {
      // Build the full path including the root folder
      const fullPath = rootFolder ? (path ? `${rootFolder.name}/${path}` : rootFolder.name) : path

      const endpoint = fullPath
        ? `agents/licitatii/${agent.id}/storage/folders?path=${encodeURIComponent(fullPath)}`
        : `agents/licitatii/${agent.id}/storage/folders`

      const result = await apiRequest(process.env.REACT_APP_API + endpoint, 'GET')

      if (!result.ok) {
        throw new Error(result.data?.message || 'Failed to load storage folders')
      }

      setFolders(result.data || [])
    } catch (error) {
      console.error('[ERROR] Problem loading storage folders:', error)
      setError(error.message)
      setFolders([])
    }

    setLoading(false)
  }

  const handleFolderClick = async folder => {
    const newPath = currentPath ? `${currentPath}/${folder.name}` : folder.name
    console.log('Folder clicked:', folder.name, 'Current path:', currentPath, 'New path:', newPath)
    setCurrentPath(newPath)
    setPathHistory([...pathHistory, newPath])
    await loadStorageFolders(newPath)
  }

  const handleBackNavigation = () => {
    if (pathHistory.length > 1) {
      const newHistory = pathHistory.slice(0, -1)
      const previousPath = newHistory[newHistory.length - 1]
      setPathHistory(newHistory)
      setCurrentPath(previousPath)
      loadStorageFolders(previousPath)
    }
  }

  const handleBreadcrumbClick = path => {
    // Build new path history based on the clicked path
    const pathParts = path ? path.split('/').filter(part => part.trim() !== '') : []
    const newHistory = [''] // Always start with root (first folder)

    // Add each part of the path to history
    let buildPath = ''
    pathParts.forEach(part => {
      buildPath = buildPath ? `${buildPath}/${part}` : part
      newHistory.push(buildPath)
    })

    setPathHistory(newHistory)
    setCurrentPath(path)
    loadStorageFolders(path)
  }

  const handleFileDownload = async file => {
    try {
      console.log('Downloading file:', file.name)

      // Calculate relative path from current location
      const relativePath = currentPath ? `${currentPath}/${file.name}` : file.name

      // Make a fetch request using the same authentication as other API calls
      const response = await fetch(process.env.REACT_APP_API + `agents/licitatii/${agent.id}/storage/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Use cookie-based auth like other API calls
        body: JSON.stringify({
          storageId: agent.storageId,
          filePath: relativePath,
          fileName: file.name,
        }),
      })

      if (!response.ok) {
        if (response.status === 401) {
          // Handle unauthorized - redirect to login like other API calls
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
          return
        }
        throw new Error(`Download failed: ${response.statusText}`)
      }

      // Get the blob from response
      const blob = await response.blob()

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = file.name
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      // Create download link
      const downloadUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('[ERROR] Problem downloading file:', error)
      setError(`Failed to download ${file.name}: ${error.message}`)
    }
  }

  const renderFolderContent = item => {
    const isFolder = item.type === 'folder'

    return (
      <List.Item
        key={item.id || item.name}
        onClick={isFolder ? () => handleFolderClick(item) : undefined}
        style={{
          cursor: isFolder ? 'pointer' : 'default',
          transition: 'background-color 0.2s',
          padding: '12px',
          borderRadius: '6px',
        }}
        onMouseEnter={e => {
          if (isFolder) {
            e.currentTarget.style.backgroundColor = '#f5f5f5'
          }
        }}
        onMouseLeave={e => {
          if (isFolder) {
            e.currentTarget.style.backgroundColor = 'transparent'
          }
        }}
        actions={
          !isFolder
            ? [
                <Button
                  key="download"
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={e => {
                    e.stopPropagation()
                    handleFileDownload(item)
                  }}
                  size="small"
                >
                  {languageContext.t('Download')}
                </Button>,
              ]
            : undefined
        }
      >
        <List.Item.Meta
          avatar={getFileIcon(item.name, item.type)}
          title={
            <div>
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{item.name}</span>
              {item.itemCount && isFolder && (
                <Tag color="blue" className="ml-2">
                  {item.itemCount} {languageContext.t('items')}
                </Tag>
              )}
            </div>
          }
          description={
            <div>
              {item.path && (
                <div>
                  <strong>{languageContext.t('Path')}:</strong> {item.path}
                </div>
              )}
              {item.lastModified && (
                <div>
                  <strong>{languageContext.t('Last Modified')}:</strong> {new Date(item.lastModified).toLocaleString()}
                </div>
              )}
              {item.size && (
                <div>
                  <strong>{languageContext.t('Size')}:</strong> {formatFileSize(item.size)}
                </div>
              )}
            </div>
          }
        />
      </List.Item>
    )
  }

  const formatFileSize = bytes => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (!agent.storageId) {
    return (
      <Card title={languageContext.t('Storage Folders')}>
        <Alert
          type="warning"
          message={languageContext.t('No Storage Configured')}
          description={languageContext.t('Please configure a OneDrive storage connector for this agent to view folders.')}
          showIcon
        />
      </Card>
    )
  }

  const getBreadcrumbItems = () => {
    const items = [
      {
        title: (
          <span onClick={() => handleBreadcrumbClick('')} style={{ cursor: 'pointer' }}>
            <HomeOutlined /> {rootFolder ? rootFolder.name : languageContext.t('Root')}
          </span>
        ),
      },
    ]

    if (currentPath && currentPath.trim() !== '') {
      const pathParts = currentPath.split('/').filter(part => part.trim() !== '')
      let buildPath = ''

      pathParts.forEach((part, index) => {
        buildPath = index === 0 ? part : `${buildPath}/${part}`
        const pathForClick = buildPath

        items.push({
          title: (
            <span
              onClick={() => {
                console.log('Breadcrumb clicked:', pathForClick)
                handleBreadcrumbClick(pathForClick)
              }}
              style={{ cursor: 'pointer' }}
            >
              {part}
            </span>
          ),
        })
      })
    }

    return items
  }

  return (
    <Card
      title={
        <div>
          <div style={{ marginBottom: '8px' }}>{languageContext.t('Storage Folders')}</div>
          <Breadcrumb items={getBreadcrumbItems()} />
        </div>
      }
      extra={
        <div>
          {currentPath && (
            <Button icon={<ArrowLeftOutlined />} onClick={handleBackNavigation} style={{ marginRight: '8px' }} size="small">
              {languageContext.t('Back')}
            </Button>
          )}
          <Tag color="blue">
            {languageContext.t('Storage ID')}: {agent.storageId}
          </Tag>
        </div>
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '10px' }}>{languageContext.t('Loading storage folders...')}</div>
        </div>
      )}

      {error && (
        <Alert type="error" message={languageContext.t('Error Loading Folders')} description={error} showIcon style={{ marginBottom: '16px' }} />
      )}

      {!loading && !error && folders.length === 0 && (
        <Alert
          type="info"
          message={languageContext.t('No Folders Found')}
          description={languageContext.t('No folders were found in the configured storage.')}
          showIcon
        />
      )}

      {!loading && !error && folders.length > 0 && (
        <div>
          {/* <div style={{ marginBottom: '16px' }}>
            <Title level={5}>
              {languageContext.t('Available Folders')} ({folders.length})
            </Title>
          </div> */}
          <List
            itemLayout="vertical"
            dataSource={folders}
            renderItem={renderFolderContent}
            pagination={
              folders.length > 10
                ? {
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} ${languageContext.t('of')} ${total} ${languageContext.t('folders')}`,
                  }
                : false
            }
          />
        </div>
      )}
    </Card>
  )
}

export default LicitatiiStorageTab
